import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Icon from '@mdi/react';
import { mdiCheck, mdiChevronRight, mdiPaperclip } from '@mdi/js';
import { Tooltip } from '../base';
import { AttachableEntityGroup, AttachableEntityItem } from '../../services/ai-adapter/smart-context';
import * as S from './ai-assistant.styles';

export interface IAttachCascadingSelectProps {
    groups: AttachableEntityGroup[];
    attachedIdsByType: Record<string, string[]>;
    disabled?: boolean;
    onToggleItem: (groupKey: string, item: AttachableEntityItem) => void;
    onToggleAll?: (groupKey: string, items: AttachableEntityItem[], selectAll: boolean) => void;
}

const VIEWPORT_MARGIN = 8;
const MAX_MENU_HEIGHT = 280;

type CascadePlacement = {
    top: number;
    left: number;
    maxHeight: number;
    submenuBelow: boolean;
};

/**
 * Cascading attach picker: type list → item list.
 * Clicking an item attaches/detaches immediately; menu stays open for multi-select.
 * Position is clamped so the menu/submenu stay inside the viewport.
 */
export const AttachCascadingSelect: React.FC<IAttachCascadingSelectProps> = ({
    groups,
    attachedIdsByType,
    disabled,
    onToggleItem,
    onToggleAll,
}) => {
    const [open, setOpen] = useState(false);
    const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);
    const [placement, setPlacement] = useState<CascadePlacement>({
        top: 0,
        left: 0,
        maxHeight: MAX_MENU_HEIGHT,
        submenuBelow: false,
    });
    const [ready, setReady] = useState(false);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        const handleClickOutside = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                setOpen(false);
                setActiveGroupKey(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    useEffect(() => {
        if (!open) {
            setActiveGroupKey(null);
            setReady(false);
        }
    }, [open]);

    useLayoutEffect(() => {
        if (!open) {
            return;
        }

        const updatePlacement = () => {
            const anchor = rootRef.current;
            const menu = menuRef.current;
            if (!anchor || !menu) {
                return;
            }

            const anchorRect = anchor.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const spaceAbove = anchorRect.top - VIEWPORT_MARGIN;
            const spaceBelow = vh - anchorRect.bottom - VIEWPORT_MARGIN;

            const menuRect = menu.getBoundingClientRect();
            const availableWidth = vw - VIEWPORT_MARGIN * 2;
            // Prefer side-by-side; stack submenu under types when it can't fit horizontally.
            const estimatedSideBySideWidth = activeGroupKey ? 160 + 220 : 160;
            const submenuBelow =
                !!activeGroupKey &&
                (estimatedSideBySideWidth > availableWidth || menuRect.width > availableWidth + 1);

            const menuWidth = Math.min(
                Math.max(menuRect.width || estimatedSideBySideWidth, 160),
                availableWidth,
            );
            const naturalHeight = menuRect.height || 160;

            const openUp = spaceAbove >= Math.min(naturalHeight, MAX_MENU_HEIGHT) || spaceAbove >= spaceBelow;
            const availableVertical = Math.max(120, openUp ? spaceAbove : spaceBelow);
            const maxHeight = Math.min(MAX_MENU_HEIGHT, availableVertical);

            let top = openUp
                ? Math.max(VIEWPORT_MARGIN, anchorRect.top - Math.min(naturalHeight, maxHeight) - VIEWPORT_MARGIN)
                : anchorRect.bottom + VIEWPORT_MARGIN;

            if (top + Math.min(naturalHeight, maxHeight) > vh - VIEWPORT_MARGIN) {
                top = Math.max(VIEWPORT_MARGIN, vh - VIEWPORT_MARGIN - Math.min(naturalHeight, maxHeight));
            }

            // Prefer aligning the menu's right edge with the button's right edge.
            let left = anchorRect.right - menuWidth;
            if (left < VIEWPORT_MARGIN) {
                left = VIEWPORT_MARGIN;
            }
            if (left + menuWidth > vw - VIEWPORT_MARGIN) {
                left = Math.max(VIEWPORT_MARGIN, vw - VIEWPORT_MARGIN - menuWidth);
            }

            setPlacement({
                top,
                left,
                maxHeight,
                submenuBelow,
            });
            setReady(true);
        };

        updatePlacement();
        // Second pass after layout settles (submenu open/close changes width/height).
        const raf = window.requestAnimationFrame(updatePlacement);

        window.addEventListener('resize', updatePlacement);
        window.addEventListener('scroll', updatePlacement, true);
        return () => {
            window.cancelAnimationFrame(raf);
            window.removeEventListener('resize', updatePlacement);
            window.removeEventListener('scroll', updatePlacement, true);
        };
    }, [open, activeGroupKey, groups.length, attachedIdsByType]);

    const activeGroup = groups.find(g => g.key === activeGroupKey) || null;
    const activeSelectableItems = activeGroup ? activeGroup.items.filter(item => !item.disabled) : [];
    const activeSelectedIds = activeGroup ? attachedIdsByType[activeGroup.key] || [] : [];
    const allActiveSelected =
        activeSelectableItems.length > 0 &&
        activeSelectableItems.every(item => activeSelectedIds.includes(item.id));

    return (
        <S.AttachMenuAnchor ref={rootRef}>
            <Tooltip direction="top" variant="dark" title={<span>Attach context</span>}>
                <S.IconButton
                    variant="text"
                    type="button"
                    disabled={disabled}
                    onClick={() => setOpen(v => !v)}
                    aria-expanded={open}
                    aria-haspopup="menu">
                    <Icon path={mdiPaperclip} size={0.7} />
                </S.IconButton>
            </Tooltip>
            {open && (
                <S.CascadeRoot
                    ref={menuRef}
                    role="menu"
                    $ready={ready}
                    $submenuBelow={placement.submenuBelow}
                    style={{
                        top: placement.top,
                        left: placement.left,
                        maxHeight: placement.maxHeight,
                    }}>
                    <S.CascadeTypeList $submenuBelow={placement.submenuBelow}>
                        {groups.map(group => {
                            const attachedCount = attachedIdsByType[group.key]?.length || 0;
                            const isActive = activeGroupKey === group.key;
                            return (
                                <S.CascadeTypeRow
                                    key={group.key}
                                    type="button"
                                    active={isActive}
                                    onClick={() =>
                                        setActiveGroupKey(prev => (prev === group.key ? null : group.key))
                                    }>
                                    <S.CascadeTypeLabel>
                                        {group.label}
                                        <S.CascadeTypeMeta>
                                            {attachedCount > 0 ? `${attachedCount}/` : ''}
                                            {group.items.length}
                                        </S.CascadeTypeMeta>
                                    </S.CascadeTypeLabel>
                                    <Icon path={mdiChevronRight} size={0.65} />
                                </S.CascadeTypeRow>
                            );
                        })}
                    </S.CascadeTypeList>
                    {activeGroup && (
                        <S.CascadeItemPanel role="menu" aria-label={activeGroup.label}>
                            {activeGroup.items.length === 0 ? (
                                <S.CascadeEmpty>No items</S.CascadeEmpty>
                            ) : (
                                <>
                                    {activeSelectableItems.length > 0 && (
                                        <S.CascadeSelectAllRow
                                            type="button"
                                            selected={allActiveSelected}
                                            onClick={() =>
                                                onToggleAll?.(
                                                    activeGroup.key,
                                                    activeSelectableItems,
                                                    !allActiveSelected,
                                                )
                                            }>
                                            <S.CascadeItemCheck selected={allActiveSelected}>
                                                {allActiveSelected && <Icon path={mdiCheck} size={0.55} />}
                                            </S.CascadeItemCheck>
                                            <S.CascadeItemSummary>Select all</S.CascadeItemSummary>
                                        </S.CascadeSelectAllRow>
                                    )}
                                    <S.CascadeItemList>
                                        {activeGroup.items.map(item => {
                                            const selected = activeSelectedIds.includes(item.id);
                                            const itemDisabled = !!item.disabled;
                                            const row = (
                                                <S.CascadeItemRow
                                                    type="button"
                                                    selected={selected}
                                                    disabled={itemDisabled}
                                                    title={itemDisabled ? undefined : item.summary}
                                                    onClick={() => {
                                                        if (itemDisabled) {
                                                            return;
                                                        }
                                                        onToggleItem(activeGroup.key, item);
                                                    }}>
                                                    <S.CascadeItemCheck selected={selected}>
                                                        {selected && <Icon path={mdiCheck} size={0.55} />}
                                                    </S.CascadeItemCheck>
                                                    <S.CascadeItemSummary>{item.summary}</S.CascadeItemSummary>
                                                </S.CascadeItemRow>
                                            );

                                            if (itemDisabled && item.disabledReason) {
                                                return (
                                                    <Tooltip
                                                        key={item.id}
                                                        direction="left"
                                                        variant="dark"
                                                        title={<span>{item.disabledReason}</span>}>
                                                        <div>{row}</div>
                                                    </Tooltip>
                                                );
                                            }

                                            return <React.Fragment key={item.id}>{row}</React.Fragment>;
                                        })}
                                    </S.CascadeItemList>
                                </>
                            )}
                        </S.CascadeItemPanel>
                    )}
                </S.CascadeRoot>
            )}
        </S.AttachMenuAnchor>
    );
};
